// A verdict just recorded on To inspect, held on its card so the operator sees
// it land (UF-02 / UF-04).
//
// Without this the card would vanish the moment the queue refetches: an
// approved pallet moves to Approved, and a rejected one leaves the queue
// altogether (the queue reads the loading area and approved pallets only). So
// the page keeps the card where it was, saying what happened:
//   - Approved — for a moment, then it moves to the Approved view. "A
//     moment" is the hold AND the queue's refetch: a card still listed on its
//     view by the last read stays Approved past the timer, or a slow refetch
//     would hand it back its live Approve / Reject (and a second tap would be
//     refused as someone else's update);
//   - Rejected — naming the pallet and its run, with Open pallet, until the
//     operator dismisses it: the next stop is repack, somewhere else.
import { useCallback, useEffect, useRef, useState } from 'react'
import { byAge, queueViewOf, type QueueRow, type QueueView } from './inspectionQueue'

export type Verdict = 'APPROVED' | 'REJECTED'
export interface Held<T> { pallet: T; outcome: Verdict; /** the hold ran its time */ due?: boolean }

const NONE: ReadonlySet<string> = new Set()

/** How long an Approved card stays on To inspect before it moves on. */
export const APPROVED_HOLD_MS = 2500

/** `stillQueued`: the pallets the last queue read still lists on To inspect /
 *  With PPECB. An Approved hold whose time is up waits while its pallet is
 *  one of them. */
export function useHeldVerdicts<T extends { pallet_id: string }>(stillQueued: ReadonlySet<string> = NONE) {
  const [held, setHeld] = useState<ReadonlyMap<string, Held<T>>>(() => new Map())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  // UF-02: the pallets given a verdict during this visit (by id, so a second
  // tap on one pallet is not counted twice). Session-local, never persisted.
  const [decided, setDecided] = useState<ReadonlyMap<string, Verdict>>(() => new Map())

  useEffect(() => {
    const live = timers.current
    return () => { live.forEach(clearTimeout); live.clear() }
  }, [])

  const release = useCallback((id: string) => {
    const t = timers.current.get(id)
    if (t) { clearTimeout(t); timers.current.delete(id) }
    setHeld(prev => {
      if (!prev.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  // The time is up: release once the queue no longer lists the pallet.
  const expire = useCallback((id: string) => {
    timers.current.delete(id)
    setHeld(prev => {
      const h = prev.get(id)
      return h ? new Map(prev).set(id, { ...h, due: true }) : prev
    })
  }, [])

  useEffect(() => {
    for (const h of held.values()) {
      if (h.due && !stillQueued.has(h.pallet.pallet_id)) release(h.pallet.pallet_id)
    }
  }, [held, stillQueued, release])

  const hold = useCallback((pallet: T, outcome: Verdict) => {
    const id = pallet.pallet_id
    const prior = timers.current.get(id)
    if (prior) { clearTimeout(prior); timers.current.delete(id) }
    setHeld(prev => new Map(prev).set(id, { pallet, outcome }))
    setDecided(prev => new Map(prev).set(id, outcome))
    if (outcome === 'APPROVED') timers.current.set(id, setTimeout(() => expire(id), APPROVED_HOLD_MS))
  }, [expire])

  let approvedThisVisit = 0
  for (const v of decided.values()) if (v === 'APPROVED') approvedThisVisit++
  return { held, hold, release, approvedThisVisit, decidedThisVisit: decided.size }
}

/**
 * To inspect's rows with the held cards put back: a held pallet the queue no
 * longer lists as To inspect (it was approved, or rejected off the queue) keeps
 * its place — the same age order it had.
 */
export function withHeld<T extends QueueRow>(rows: readonly T[], held: ReadonlyMap<string, Held<T>>): T[] {
  if (held.size === 0) return [...rows]
  const here = new Set(rows.map(p => p.pallet_id))
  const back = [...held.values()].filter(h => !here.has(h.pallet.pallet_id)).map(h => h.pallet)
  return [...rows, ...back].sort(byAge)
}

/**
 * The held cards that belong on one view: the view the pallet was on when its
 * verdict was recorded. A pallet approved from With PPECB is held there, not
 * on To inspect — the card stays where the operator tapped it.
 */
export function heldOn<T extends QueueRow>(held: ReadonlyMap<string, Held<T>>, view: QueueView): ReadonlyMap<string, Held<T>> {
  if (held.size === 0) return held
  return new Map([...held].filter(([, h]) => queueViewOf(h.pallet) === view))
}
